/**
 * Benevita Design System (BDS) v1.0 — Web Tokens
 * ─────────────────────────────────────────────────────────────────────────────
 * Derived from /design/tokens.json (canonical source). If a value changes
 * there, mirror it here. Never hardcode hex values in components.
 *
 * Usage:
 *   import { COLOR, RADIUS, FONT, GRADIENT, statusBadge } from './tokens';
 */

// ── Palette ───────────────────────────────────────────────────────────────────
export const COLOR = {
  // ── Primary / clinical blue ──────────────────────────────────────────────
  blue:        '#7b1e3c',   // Benevita maroon (primary actions)
  blueLight:   '#94335a',   // hover / lighter variant
  blueBg:      '#f7eef3',   // tinted maroon background
  blueBorder:  '#d3b0c0',   // border / outline

  // ── Success / active / confirmed ─────────────────────────────────────────
  green:       '#00897b',   // clinical teal-green
  greenBg:     '#e8f5f3',
  greenBorder: '#80cbc4',
  greenDark:   '#00695c',

  // ── Danger / critical / inactive ─────────────────────────────────────────
  red:         '#d32f2f',   // clinical alert red
  redBg:       '#fdecea',
  redBorder:   '#ef9a9a',
  redDark:     '#b71c1c',

  // ── Warning / pending / caution ──────────────────────────────────────────
  amber:       '#f57c00',   // clinical amber
  amberBg:     '#fff8e1',
  amberBorder: '#ffcc80',
  amberDark:   '#e65100',

  // ── Accent / secondary (links, highlights) ───────────────────────────────
  purple:      '#5c35b5',   // clinical purple (prescriptions, specialist)
  purpleBg:    '#f0ebfc',

  // ── Monitoring / vitals (remote obs, LOINC) ──────────────────────────────
  teal:        '#0277bd',   // info / monitoring blue
  tealBg:      '#e1f0f9',
  tealDark:    '#01579b',

  // ── Neutrals / text ──────────────────────────────────────────────────────
  gray:        '#8b7681',
  grayLight:   '#90a4ae',
  grayBg:      '#f6eff2',
  grayBorder:  '#ece3e7',
  graySubtle:  '#edf1f5',
  text:        '#2b1b22',   // warm near-black, maroon undertone
  textSub:     '#5e4a53',
  textMuted:   '#8b7681',

  // ── Page background ───────────────────────────────────────────────────────
  pageBg: '#f7f3f4',        // warm maroon-grey tint

  // ── Sidebar (lighter Qatar burgundy) ──────────────────────────────────────
  sidebarBg:         '#9a4674',
  sidebarBgHover:    'rgba(255,255,255,0.10)',
  sidebarBgActive:   'rgba(201,162,75,0.26)',
  sidebarBorder:     '#b05a89',
  sidebarText:       '#f3e6ec',
  sidebarTextMuted:  '#cba9ba',
  sidebarGroupLabel: '#d7a9bf',
  sidebarAccent:     '#e3c77e',   // gold active / logo accent

  // ── BDS canonical aliases (preferred names going forward) ────────────────
  primary:     '#7b1e3c',
  primaryDark: '#7b1e3c',
  navy:        '#4a1228',
  sky:         '#c39a4e',
  accent:      '#c39a4e',
  gold:        '#c39a4e',
  goldLight:   '#e3c77e',
  goldDark:    '#9a7634',
  silver:      '#aeb6bd',
  silverLight: '#d7dce0',
  surface:     '#ffffff',
  border:      '#ece3e7',
  bgMarketing: '#f8fafc',
  textFaint:   '#90a4ae',
  onDarkText:  '#c4d4e3',
  onDarkMuted: '#90a4ae',
};

// ── Gradients (BDS) ───────────────────────────────────────────────────────────
export const GRADIENT = {
  hero:  'linear-gradient(135deg, #4a1228 0%, #7b1e3c 55%, #94335a 100%)',
  brand: 'linear-gradient(135deg, #7b1e3c, #c39a4e)',
};

// ── Radius ────────────────────────────────────────────────────────────────────
export const RADIUS = {
  sm:   4,
  md:   8,
  lg:   12,
  xl:   14,
  pill: 20,
};

// ── Font ──────────────────────────────────────────────────────────────────────
export const FONT = {
  family: 'Inter, system-ui, sans-serif',
  size: {
    xs:   10,
    sm:   11,
    base: 13,
    md:   15,
    lg:   17,
    xl:   22,
  },
  weight: {
    normal:  400,
    medium:  500,
    semi:    600,
    bold:    700,
    xbold:   800,
  },
};

// ── Spacing scale (multiples of 4px) ─────────────────────────────────────────
export const SPACE = {
  1:  4,
  2:  8,
  3:  12,
  4:  16,
  5:  20,
  6:  24,
  8:  32,
  10: 40,
  12: 48,
};

// ── Elevation (use instead of borders to separate layers) ─────────────────────
export const ELEVATION = {
  none:  'none',
  xs:    '0 1px 2px rgba(13,27,46,0.05)',
  sm:    '0 1px 4px rgba(13,27,46,0.07)',
  md:    '0 4px 12px rgba(13,27,46,0.10)',
  lg:    '0 8px 24px rgba(13,27,46,0.14)',
  xl:    '0 20px 48px rgba(13,27,46,0.20)',
  hover: '0 6px 16px rgba(13,27,46,0.12)',
};

// ── State colours (hover / active / focus / disabled overlays) ────────────────
export const STATE = {
  hoverOverlay:   'rgba(13,27,46,0.04)',
  activeOverlay:  'rgba(13,27,46,0.08)',
  focusRing:      '0 0 0 3px rgba(123,30,60,0.22)',
  disabledOpacity: 0.45,
};

// ── Urgency levels (clinical priority system) ─────────────────────────────────
export const URGENCY = {
  critical: { bg: '#fff0f0', border: '#fca5a5', text: '#b71c1c', badge: '#d32f2f' },
  warning:  { bg: '#fffbf0', border: '#fcd34d', text: '#92400e', badge: '#d97706' },
  info:     { bg: '#f7eef3', border: '#d3b0c0', text: '#7b1e3c', badge: '#7b1e3c' },
  success:  { bg: '#f0faf7', border: '#6ee7b7', text: '#064e3b', badge: '#00897b' },
};

// ── Shadows ───────────────────────────────────────────────────────────────────
export const shadow = {
  sm:  ELEVATION.sm,
  md:  ELEVATION.md,
  lg:  ELEVATION.xl,
};

// ── Common composed style objects (use these in JSX style= props) ─────────────
export const baseInputStyle = {
  boxSizing:    'border-box',
  padding:      '8px 12px',
  border:       '1px solid #ECE3E7',
  borderRadius: 10,
  fontSize:     FONT.size.base,
  fontFamily:   FONT.family,
  outline:      'none',
  background:   '#fff',
  width:        '100%',
  color:        COLOR.text,
  transition:   'border-color .15s, box-shadow .15s',
};

export const labelStyle = {
  fontSize:   FONT.size.sm,
  fontWeight: FONT.weight.semi,
  color:      COLOR.textSub,
  display:    'block',
  marginBottom: 4,
};

export const thStyle = {
  padding:       '10px 14px',
  textAlign:     'left',
  fontSize:      FONT.size.sm,
  fontWeight:    FONT.weight.bold,
  color:         COLOR.gray,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  borderBottom:  `1px solid ${COLOR.grayBorder}`,
  background:    COLOR.grayBg,
  whiteSpace:    'nowrap',
};

export const tdStyle = {
  padding:       '11px 14px',
  fontSize:      FONT.size.base,
  color:         COLOR.textSub,
  borderBottom:  `1px solid ${COLOR.graySubtle}`,
  verticalAlign: 'middle',
};

// ── Badge colour map (used by Badge component) ────────────────────────────────
export const BADGE_COLORS = {
  blue:   { background: COLOR.blueBg,   color: COLOR.blue },
  green:  { background: COLOR.greenBg,  color: COLOR.greenDark },
  red:    { background: COLOR.redBg,    color: COLOR.redDark },
  amber:  { background: COLOR.amberBg,  color: COLOR.amberDark },
  purple: { background: COLOR.purpleBg, color: COLOR.purple },
  teal:   { background: COLOR.tealBg,   color: COLOR.tealDark },
  gray:   { background: COLOR.graySubtle, color: COLOR.gray },
};

// ── Canonical status → badge style map (BDS) ──────────────────────────────────
// Single source of truth for every status chip across the app.
// Derived from design/tokens.json statusMap.
const STATUS_TIER = {
  success: ['ACTIVE', 'COMPLETED', 'PAID', 'APPROVED', 'AVAILABLE'],
  warning: ['PENDING', 'SCHEDULED', 'ASSIGNED', 'ON_HOLD', 'PARTIALLY_PAID', 'MEDIUM'],
  info:    ['IN_PROGRESS', 'NURSE_EN_ROUTE', 'ARRIVED', 'REQUESTED', 'INSURANCE_PROCESSING', 'LOW'],
  danger:  ['CANCELLED', 'OVERDUE', 'CRITICAL', 'DECEASED', 'REJECTED', 'NO_SHOW', 'VOIDED', 'SUSPENDED', 'URGENT', 'HIGH', 'SEVERE'],
  neutral: ['DRAFT', 'INACTIVE', 'UNKNOWN', 'RESCHEDULED', 'WAIVED', 'REFUNDED'],
};

const TIER_STYLE = {
  success: { background: COLOR.greenBg,   color: COLOR.greenDark },
  warning: { background: COLOR.amberBg,   color: COLOR.amberDark },
  info:    { background: COLOR.tealBg,    color: COLOR.tealDark },
  danger:  { background: COLOR.redBg,     color: COLOR.redDark },
  neutral: { background: COLOR.graySubtle, color: COLOR.gray },
};

const STATUS_LOOKUP = Object.fromEntries(
  Object.entries(STATUS_TIER).flatMap(([tier, statuses]) =>
    statuses.map((s) => [s, TIER_STYLE[tier]])
  )
);

/** statusBadge('COMPLETED') → { background, color }. Unknown statuses → neutral. */
export function statusBadge(status) {
  return STATUS_LOOKUP[String(status || '').toUpperCase()] || TIER_STYLE.neutral;
}

// ── Button variant map (used by Button component) ─────────────────────────────
export const BUTTON_VARIANTS = {
  primary:   {
    background: 'linear-gradient(135deg, #7b1e3c, #94335a)',
    color: '#fff', border: 'none',
    boxShadow: '0 4px 14px -4px rgba(123,30,60,.45)',
  },
  danger:    { background: COLOR.red,        color: '#fff', border: 'none' },
  success:   { background: COLOR.green,      color: '#fff', border: 'none' },
  amber:     { background: COLOR.amber,      color: '#fff', border: 'none' },
  teal:      { background: COLOR.teal,       color: '#fff', border: 'none' },
  ghost:     { background: 'transparent',    color: COLOR.gray,  border: `1px solid ${COLOR.grayBorder}` },
  ghostBlue: { background: COLOR.blueBg,     color: COLOR.blue,  border: `1px solid ${COLOR.blueBorder}` },
  ghostRed:  { background: COLOR.redBg,      color: COLOR.red,   border: `1px solid ${COLOR.redBorder}` },
};
